import Typography from 'components/Typography';
import styles from './index.module.scss';
import { useMemoTranslations } from 'hooks/Intl';
import * as translations from 'intls/apiKey';
import * as modalTranslations from 'intls/modals';
import { useTheme } from 'hooks/theme';
import { formatISO9075, parseISO } from 'date-fns';
import ButtonWithIcon from 'components/ButtonWithIcon/ButtonWithIcon';
import EditIcon from 'assets/icons/EditIcon';
import { useModal } from 'hooks/Modal';
import EditModal from './EditModal';
import { useApiKey } from 'hooks/ApiKey';
import ReloadIcon from 'assets/icons/ReloadIcon';
import StyledModal, {
  ModalType,
} from 'components/Modal/styledModal/StyledModal';
import { apiKeyService } from 'services';
import DenyIcon from 'assets/icons/DenyIcon';
import { AxiosError } from 'axios';
import { ALREADY_INVALID_API_KEY } from 'constants/errors';

interface ApiKeyItemProps {
  apiKey: ApiKey;
}

const ApiKeyItem = ({ apiKey }: ApiKeyItemProps) => {
  const { theme } = useTheme();
  const intl = useMemoTranslations(translations);
  const intlModals = useMemoTranslations(modalTranslations);
  const { actions } = useModal();
  const { fetchApiKeys } = useApiKey();

  const rotateKey = async () => {
    try {
      actions.showLoading();

      await apiKeyService.rotate(apiKey.api_key_id);

      actions.hideLoading();
      actions.showModal(
        '',
        <StyledModal
          type={ModalType.SUCCESS}
          title={intl.KEY_ROTATED_SUCCESSFULLY}
        />,
        false
      );
    } catch (error) {
      actions.hideLoading();
      actions.showModal(
        '',
        <StyledModal
          type={ModalType.ERROR}
          title={intlModals.UNEXPECTED_ERROR}
        />,
        false
      );
    } finally {
      fetchApiKeys();
    }
  };

  const invalidateKey = async () => {
    try {
      actions.showLoading();

      await apiKeyService.invalidate(apiKey.api_key_id);

      actions.hideLoading();
      actions.showModal(
        '',
        <StyledModal
          type={ModalType.SUCCESS}
          title={intl.KEY_INVALIDATED_SUCCESSFULLY}
        />,
        false
      );
    } catch (error) {
      const description = (error as AxiosError<ApiError>)?.response?.data
        ?.error_description;

      actions.hideLoading();
      actions.showModal(
        '',
        <StyledModal
          type={ModalType.ERROR}
          title={
            description === ALREADY_INVALID_API_KEY
              ? intl.ALREADY_INVALID_API_KEY
              : intlModals.UNEXPECTED_ERROR
          }
        />,
        false
      );
    } finally {
      fetchApiKeys();
    }
  };

  const showEditModal = () => {
    actions.showModal(
      intl.EDIT_PEMISSIONS,
      <EditModal apiKey={apiKey} fetchApiKeys={fetchApiKeys} />,
      true
    );
  };

  const openConfirmRotateModal = () => {
    actions.showModal(
      '',
      <StyledModal
        title={intl.ROTATE_KEY_CONFIRMATION}
        type={ModalType.CONFIRMATION}
        textConfirm={intl.LABEL_CONFIRM_ROTATE_KEY}
        onClickConfirm={rotateKey}
        onClickDeny={actions.closeModal}
      />,
      true
    );
  };

  const openConfirmInvalidateModal = () => {
    actions.showModal(
      '',
      <StyledModal
        title={intl.INVALIDATE_KEY_CONFIRMATION}
        type={ModalType.CONFIRMATION}
        textConfirm={intl.LABEL_CONFIRM_INVALIDATE_KEY}
        onClickConfirm={invalidateKey}
        onClickDeny={actions.closeModal}
      />,
      true
    );
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Typography type="h6">
          {intl.KEY}:{' '}
          <span className={`${styles.key} ${styles[theme]}`}>
            {apiKey.api_key}
          </span>
        </Typography>
        <Typography type="h6">
          {intl.CREATED}:{' '}
          <span className={styles.creationDate}>
            {formatISO9075(parseISO(apiKey.created_at))}
          </span>
        </Typography>
      </div>
      <div className={styles.options}>
        <ButtonWithIcon
          Icon={EditIcon}
          label={intl.EDIT_PEMISSIONS}
          onClick={showEditModal}
        />
        <ButtonWithIcon
          Icon={ReloadIcon}
          label={intl.ROTATE_KEY}
          onClick={openConfirmRotateModal}
        />
        {apiKey.is_valid ? (
          <ButtonWithIcon
            Icon={DenyIcon}
            label={intl.INVALIDATE_KEY}
            onClick={openConfirmInvalidateModal}
            disabled={!apiKey.is_valid}
          />
        ) : (
          <div className={styles.invalidLabel}>
            <Typography color="alert">{intl.INVALIDATED_API_KEY}</Typography>
          </div>
        )}
      </div>
    </div>
  );
};

export default ApiKeyItem;
